// import api from '../util/api'
import BaseProvider from './BaseProvider'
import {
  ProviderAutomation,
  ProviderAutomationFetchParams,
  ProviderAutomationParams,
  ProviderChecklist,
  ProviderChecklistFetchParams,
  ProviderChecklistItem,
  ProviderChecklistItemParams,
  ProviderChecklistItemUploadParams,
  ProviderChecklistParams,
  ProviderTemplate,
  ProviderTemplateFetchParams,
  ProviderTemplateItem,
  ProviderTemplateItemParams,
  ProviderTemplateItemUploadParams,
  ProviderTemplateParams,
  ProviderTemplateSection,
  ProviderTemplateSectionParams,
  ProviderUser,
  ProviderUserParams,
} from './types'

export class ApiClient {
  tokenResolver: () => Promise<string | null>
  host: string

  constructor(host: string, tokenResolver: () => Promise<string | null>) {
    this.tokenResolver = tokenResolver
    this.host = host
  }

  async getToken() {
    return this.tokenResolver()
  }

  async get(url: string) {
    const token = await this.getToken()
    const fullUrl = `${this.host}${url}`
    return fetch(fullUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((res) => res.json())
  }

  async post(url: string, body: unknown) {
    const token = await this.getToken()
    const fullUrl = `${this.host}${url}`
    return fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    }).then((res) => res.json())
  }

  async put(url: string, body: unknown) {
    const token = await this.getToken()
    const fullUrl = `${this.host}${url}`

    return fetch(fullUrl, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then((res) => res.json())
  }

  async del(url: string) {
    const fullUrl = `${this.host}${url}`
    return fetch(fullUrl, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${await this.getToken()}`,
      },
    }).then((res) => res.json())
  }
}

export default class ApiProvider extends BaseProvider {
  api: ApiClient

  constructor(api: ApiClient) {
    super()
    this.api = api
  }

  async fetchTemplates(params?: ProviderTemplateFetchParams) {
    let url = `/api/templates`
    if (params) {
      const query = new URLSearchParams()
      if (params.limit) query.append('limit', params.limit.toString())
      if (params.order) query.append('order', params.order)
      url = `${url}?${query.toString()}`
    }
    return this.api.get(url) as Promise<ProviderTemplate[]>
  }

  async fetchTemplate(id: string) {
    return this.api.get(`/api/templates/${id}`) as Promise<
      ProviderTemplate | undefined
    >
  }

  async createTemplate(params: ProviderTemplateParams) {
    return this.api
      .post(`/api/templates`, { template: params })
      .then((template: ProviderTemplate) => template)
  }

  async updateTemplate(id: string, params: ProviderTemplateParams) {
    return this.api.put(`/api/templates/${id}`, {
      template: params,
    }) as Promise<ProviderTemplate>
  }

  async deleteTemplate(id: string) {
    await this.api.del(`/api/templates/${id}`)

    return true
  }

  async reorderTemplateItems(
    id: string,
    sectionsAndItemsMap: Record<string, string[]>
  ): ReturnType<BaseProvider['reorderTemplateItems']> {
    return this.api.put(`/api/templates/${id}/reorder_items`, {
      sections: sectionsAndItemsMap,
    }) as Promise<ProviderTemplate>
  }

  async fetchTemplateItems(templateId: string) {
    return this.api.get(`/api/templates/${templateId}/items`) as Promise<
      ProviderTemplateItem[]
    >
  }

  async createTemplateItem(
    templateId: string,
    params: ProviderTemplateItemParams
  ): Promise<ProviderTemplateItem> {
    return this.api.post(
      `/api/templates/${templateId}/items`,
      params
    ) as Promise<ProviderTemplateItem>
  }

  async deleteTemplateItem(
    templateId: string,
    itemId: string
  ): Promise<boolean> {
    await this.api.del(`/api/templates/${templateId}/items/${itemId}`)
    return true
  }

  async updateTemplateItem(
    templateId: string,
    itemId: string,
    params: ProviderTemplateItemParams
  ): Promise<ProviderTemplateItem> {
    return this.api.put(
      `/api/templates/${templateId}/items/${itemId}`,
      params
    ) as Promise<ProviderTemplateItem>
  }

  fetchChecklists(
    params?: ProviderChecklistFetchParams
  ): Promise<ProviderChecklist[]> {
    let url = `/api/checklists`
    if (params) {
      const query = new URLSearchParams()
      if (params.complete !== undefined)
        query.append('complete', params.complete ? 'true' : 'false')
      if (params.limit) query.append('limit', params.limit.toString())
      if (params.order) query.append('order', params.order)
      if (params.assignee) query.append('assignee', params.assignee)
      url = `${url}?${query.toString()}`
    }
    return this.api.get(url) as Promise<ProviderChecklist[]>
  }

  async createChecklist(name: string): Promise<ProviderChecklist> {
    return (await this.api.post(`/api/checklists`, {
      name,
    })) as ProviderChecklist
  }

  async createChecklistFromTemplate(
    templateId: string
  ): Promise<ProviderChecklist> {
    return (await this.api.post(`/api/checklists`, {
      template_id: templateId,
    })) as ProviderChecklist
  }

  async createChecklistFromChecklist(
    templateId: string
  ): Promise<ProviderChecklist> {
    return (await this.api.post(`/api/checklists`, {
      source_id: templateId,
    })) as ProviderChecklist
  }

  async fetchChecklist(id: string) {
    return this.api
      .get(`/api/checklists/${id}`)
      .catch(() => undefined) as Promise<ProviderChecklist | undefined>
  }

  async updateChecklist(
    checklistId: string,
    params: ProviderChecklistParams
  ): Promise<ProviderChecklist> {
    return this.api.put(
      `/api/checklists/${checklistId}`,
      params
    ) as Promise<ProviderChecklist>
  }

  async createChecklistItem(
    checklistId: string,
    params: ProviderChecklistItemParams
  ): Promise<ProviderChecklistItem> {
    return this.api.post(
      `/api/checklists/${checklistId}/items`,
      params
    ) as Promise<ProviderChecklistItem>
  }

  async updateChecklistItem(
    checklistId: string,
    itemId: string,
    params: ProviderChecklistItemParams
  ): Promise<ProviderChecklistItem> {
    return this.api.put(
      `/api/checklists/${checklistId}/items/${itemId}`,
      params
    ) as Promise<ProviderChecklistItem>
  }

  async getChecklistItemUploadUrl(
    checklistId: string,
    itemId: string,
    params: ProviderChecklistItemUploadParams
  ): Promise<{ url: string; filename: string }> {
    return this.api.post(
      `/api/checklists/${checklistId}/items/${itemId}/uploadUrl`,
      params
    ) as Promise<{ url: string; filename: string }>
  }

  async getTemplateItemUploadUrl(
    templateId: string,
    itemId: string,
    params: ProviderTemplateItemUploadParams
  ): Promise<{ url: string; filename: string }> {
    return this.api.post(
      `/api/templates/${templateId}/items/${itemId}/uploadUrl`,
      params
    ) as Promise<{ url: string; filename: string }>
  }

  async getChecklistItemFileUrl(
    checklistId: string,
    itemId: string
  ): Promise<{ url: string; filename: string }> {
    return this.api.post(
      `/api/checklists/${checklistId}/items/${itemId}/fileUrl`,
      {}
    ) as Promise<{ url: string; filename: string }>
  }

  async getChecklistItemDescriptionFileUrl(
    checklistId: string,
    itemId: string
  ): Promise<{ url: string; filename: string }> {
    return this.api.post(
      `/api/checklists/${checklistId}/items/${itemId}/descriptionFileUrl`,
      {}
    ) as Promise<{ url: string; filename: string }>
  }

  async getTemplateItemDescriptionFileUrl(
    templateId: string,
    itemId: string
  ): Promise<{ url: string; filename: string }> {
    return this.api.post(
      `/api/templates/${templateId}/items/${itemId}/descriptionFileUrl`,
      {}
    ) as Promise<{ url: string; filename: string }>
  }

  async deleteChecklistItem(
    checklistId: string,
    itemId: string
  ): Promise<ProviderChecklistItem> {
    return this.api.del(
      `/api/checklists/${checklistId}/items/${itemId}`
    ) as Promise<ProviderChecklistItem>
  }

  async createTemplateSection(
    templateId: string,
    params: ProviderTemplateSectionParams
  ): Promise<ProviderTemplateSection> {
    return this.api.post(
      `/api/templates/${templateId}/sections`,
      params
    ) as Promise<ProviderTemplateSection>
  }

  async deleteTemplateSection(
    templateId: string,
    id: string
  ): Promise<boolean> {
    await this.api.del(`/api/templates/${templateId}/sections/${id}`)

    return true
  }

  async fetchTemplateSections(
    templateId: string
  ): Promise<ProviderTemplateSection[]> {
    const url = `/api/templates/${templateId}/sections`

    return this.api.get(url) as Promise<ProviderTemplateSection[]>
  }

  async deleteChecklist(checklistId: string): Promise<boolean> {
    await this.api.del(`/api/checklists/${checklistId}`)

    return true
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async fetchAutomations(params: ProviderAutomationFetchParams) {
    const url = `/api/automations`
    return this.api.get(url) as Promise<ProviderAutomation[]>
  }

  async createAutomation(params: ProviderAutomationParams) {
    return this.api
      .post(`/api/automations`, params)
      .then((result: ProviderAutomation) => result)
  }

  async updateAutomation(id: string, params: ProviderAutomationParams) {
    return this.api.put(
      `/api/automations/${id}`,
      params
    ) as Promise<ProviderAutomation>
  }

  async deleteAutomation(id: string) {
    await this.api.del(`/api/automations/${id}`)

    return true
  }

  // users
  async fetchUsers() {
    const url = `/api/users`
    return this.api.get(url) as Promise<ProviderUser[]>
  }

  async createUser(params: ProviderUserParams) {
    return this.api
      .post(`/api/users`, params)
      .then((result: ProviderUser) => result)
  }

  async updateUser(id: string, params: ProviderUserParams) {
    return this.api.put(`/api/users/${id}`, params) as Promise<ProviderUser>
  }

  async deleteUser(id: string) {
    return this.api.del(`/api/users/${id}`) as Promise<ProviderUser>
  }

  async getPushSubscription(clientId: string) {
    let url = `/api/push_subscription`
    const query = new URLSearchParams()
    query.append('clientId', clientId)
    url = `${url}?${query.toString()}`
    return this.api.get(url) as Promise<{
      payload: string
    }>
  }

  async updatePushSubscription(clientId: string, payload: string) {
    return this.api.put(`/api/push_subscription`, { payload, clientId })
  }

  async deletePushSubscription(clientId: string) {
    const query = new URLSearchParams()
    query.append('clientId', clientId)
    return this.api.del(`/api/push_subscription?${query.toString()}`)
  }
}
